import { TextButton } from '@creditornot/cb-button';
import React, { useCallback, useContext, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { updateItemBindingsOrder } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import BulkActionsToolbar from 'modules/menu/bulk-actions/components';
import useBulkSelection from 'modules/menu/bulk-actions/useBulkSelection';
import CategoryActions from 'modules/menu/categories/components/CategoryActions';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { MENU_MOBILE_BREAKPOINT } from 'modules/menu/constants';
import useMenuData from 'modules/menu/useMenuData';
import { flattenCategories, isMenuEditorItem } from 'modules/menu/utils';

import CategoryItemsDnDMonitor from './CategoryItemsDnDMonitor';
import MenuEditorCollectionList from '../../../components/MenuEditorCollectionList';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';
import useMenuEditorLists from '../../../hooks/useMenuEditorLists';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../constants';
import { transformItemInventoryToQuantity } from '../Item/utils';
import ItemSidePanel from '../ItemSidePanel';
import NewItemSidePanel from '../NewItemSidePanel';

type SubCategoryItemsProps = {
  categoryId: string;
  menuEditorRootUrl: string;
  menuId: string;
  onMoveItemsToCategory: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
  onMoveItemToNoCategory: (activeId: string) => Promise<void>;
};

const AddButton = styled(TextButton).attrs({ variant: 'brand' })`
  margin-inline-end: 16px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-end: 8px;
  }
`;

const SubCategoryItems = ({
  categoryId,
  menuEditorRootUrl,
  menuId,
  onMoveItemsToCategory,
  onMoveItemToNoCategory,
}: SubCategoryItemsProps) => {
  const { menu, categories, updateMenuData } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const subcategory = useMemo(
    () => flattenCategories(categories).find(category => category.id === categoryId),
    [categoryId, categories],
  );

  if (!subcategory) {
    throw new Error("Subcategory doesn't exist");
  }

  const menuPermissions = useContext(MenuPermissionsContext);
  const { categoryImages } = useContext(MenuFeaturesContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const {
    menuEditorList,
    activeItem,
    searchFilter,
    itemInventory,
    newItemSidePanelProps,
    setActiveItemId,
    setSearchFilter,
    handleItemCopy,
    refetchInventory,
    itemConfigs,
    updateItemConfig,
    itemCommissions,
    refetchItemCommissions,
  } = useMenuEditorLists('items-by-child-category', { categoryId });

  const activeItemQuantity = useMemo(() => {
    const itemQuantity = itemInventory?.find(inventory => inventory.itemId === activeItem?.id);
    return itemQuantity ? transformItemInventoryToQuantity(itemQuantity) : undefined;
  }, [activeItem?.id, itemInventory]);

  const activeItemConfig = useMemo(() => {
    const activeItemId = activeItem?.id;
    return activeItemId ? itemConfigs[activeItemId] : undefined;
  }, [activeItem?.id, itemConfigs]);

  const { selectedIds, selectMultiple, deselectMultiple, clearSelection } = useBulkSelection(menuEditorList);

  const itemsList = useMemo(() => menuEditorList.filter(isMenuEditorItem), [menuEditorList]);

  const handleBulkUpdateItemBindingOrder = useCallback(
    async (listIds: string[]) => {
      try {
        const updatedMenu = await updateItemBindingsOrder(menuId, subcategory.id, listIds);
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
      } catch (error) {
        toast.error(error.message, { duration: Infinity });
      }
    },
    [subcategory.id, updateMenuData, menuId],
  );

  const handleMoveItemsToCategory = useCallback(
    async (activeId: string, overId: string, selectedIds?: Set<string>): Promise<void> => {
      await onMoveItemsToCategory(activeId, overId, selectedIds);
      if (selectedIds && selectedIds.size > 1) {
        clearSelection();
      }
    },
    [clearSelection, onMoveItemsToCategory],
  );

  return (
    <CategoryItemsDnDMonitor
      menuEditorList={menuEditorList}
      category={subcategory}
      onBulkUpdateItemBindingsOrders={handleBulkUpdateItemBindingOrder}
      onUpdateItemBindingsOrder={handleBulkUpdateItemBindingOrder}
      onMoveItemToNoCategory={onMoveItemToNoCategory}
      onMoveItemsToCategory={handleMoveItemsToCategory}
    >
      {({ displayMenuEditorList }) => {
        return (
          <MenuEditorCollectionList
            title={localize(subcategory.name, primaryLanguage)}
            description={localize(subcategory.description, primaryLanguage)}
            image={
              categoryImages && subcategory.image
                ? { src: subcategory.image, blurhash: subcategory.image_blurhash || null }
                : undefined
            }
            search={searchFilter}
            onSearch={setSearchFilter}
            headerRight={
              <>
                {menuPermissions.contentEdit && (
                  <AddButton onClick={() => setActiveItemId('new-item')}>
                    <FormattedMessage
                      id={categoryId === WOLT_SPECIALS_CATEGORY_ID ? 'menu.addSpecial' : 'menu.addItem'}
                    />
                  </AddButton>
                )}

                <CategoryActions
                  type="subcategory"
                  category={subcategory}
                  menuEditorRootUrl={menuEditorRootUrl}
                  menuId={menuId}
                />
              </>
            }
            toolbar={
              itemsList.length > 0 && (
                <BulkActionsToolbar
                  commissions={itemCommissions}
                  onSelectAll={() => selectMultiple(itemsList.map(item => item.itemState.id))}
                  onClearSelection={clearSelection}
                  disabled={!!activeItem}
                  refetchCommissions={refetchItemCommissions}
                  selectAllItemsCount={itemsList.length}
                  selectedIds={selectedIds}
                />
              )
            }
            itemData={{
              menuId,
              list: displayMenuEditorList,
              itemInventory,
              canDrag: true,
              canDrop: true,
              activeItemId: activeItem?.id,
              selectedIds,
              onSelectItems: selectMultiple,
              onDeselectItems: deselectMultiple,
              onItemClick: itemId => setActiveItemId(itemId !== activeItem?.id ? itemId : undefined),
              onItemCopy: handleItemCopy,
            }}
          >
            {!!activeItem && (
              <ItemSidePanel
                item={activeItem}
                menu={menu}
                itemQuantity={activeItemQuantity}
                onItemBalanceUpdated={refetchInventory}
                onClose={() => setActiveItemId(undefined)}
                itemConfig={activeItemConfig}
                onItemConfigUpdated={updateItemConfig}
                itemCommissions={itemCommissions}
                onItemCommissionsUpdated={refetchItemCommissions}
              />
            )}
            {newItemSidePanelProps.isOpen && (
              <NewItemSidePanel {...newItemSidePanelProps} onItemConfigUpdated={updateItemConfig} />
            )}
          </MenuEditorCollectionList>
        );
      }}
    </CategoryItemsDnDMonitor>
  );
};

export default React.memo(SubCategoryItems);
